import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/query-client';
import { AuthProvider } from './context';
import { AppStaff } from './app-staff';
import { ToastContainer } from './components/ToastContainer/toast-container';

async function loadLocalQaFeatures(): Promise<void> {
  if (!import.meta.env.DEV || import.meta.env.VITE_QA_SUCCEED_API !== 'true') {
    return;
  }

  try {
    const response = await fetch('/features', { credentials: 'include' });
    if (!response.ok) {
      console.warn(`[QA Features] GET /features returned ${response.status}`);
      return;
    }

    const payload: unknown = await response.json();
    const featureMap =
      payload && typeof payload === 'object' && 'features' in payload
        ? (payload as { features?: unknown }).features
        : payload;
    if (featureMap && typeof featureMap === 'object' && !Array.isArray(featureMap)) {
      window.CP_COURSE_PLANNER_FEATURES = featureMap as Record<string, unknown>;
    }
  } catch (error) {
    console.warn('[QA Features] Could not load /features:', error);
  }
}

function mountStaff(container: HTMLElement): void {
  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter basename="">
            <AppStaff />
            <ToastContainer />
          </BrowserRouter>
        </AuthProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </StrictMode>
  );
}

async function mountApp(): Promise<void> {
  const hostId = 'cp-root';
  const host = document.getElementById(hostId);
  if (!host) {
    throw new Error(`Root element #${hostId} not found`);
  }
  await loadLocalQaFeatures();
  mountStaff(host);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountApp);
} else {
  mountApp();
}
